/**
 * Derives denial/query bucket summary props for the CPT codes screen.
 *
 * @module useDenialQueryBucket
 */

import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useDataAccumulatorForSingleOrder } from "../../../../../context/dataAccumulatorForSingleOrder";
import type { DenialQueryReasonBucket } from "../../../../../data-model/denialQueryBucket";
import type { RootState } from "../../../../../redux/store/store";

/**
 * Values displayed by the denial and query bucket summary component.
 */
export interface DenialQueryBucketDetails {
  /** Complete text displayed in the monthly-denial badge. */
  monthlyDenialText: string;
  /** Complete text displayed in the total-denial badge. */
  totalDenialText: string;
  /** Whether either bucket meets the facility's minimum reason-count threshold. */
  shouldShowDenialQueryBucket: boolean;
  /** Title displayed in the summary header. */
  title: string;
  /** Number of columns used for denial and query sections. */
  numberOfColumns: number;
  /** Accent color for denial-related borders, badges, and reason markers. */
  accentColor: string;
  /** Light background color for the summary header. */
  headerBackgroundColor: string;
  /** Denial buckets returned for the regimen. */
  denialReasons: DenialQueryReasonBucket[];
  /** Query buckets returned for the regimen. */
  queryReasons: DenialQueryReasonBucket[];
  /** Whether the denial bucket is greater than zero. */
  isDenialBucketGreaterThanZero: boolean;
  /** Whether the query bucket is greater than zero. */
  isQueryBucketGreaterThanZero: boolean;
}

/**
 * Derives denial-query summary props from the facility threshold and context response.
 *
 * Returns a hidden, empty presentation until the response has loaded. When
 * `reasons_min_count` is unset, defaults to `999`. A bucket is shown only when
 * it has at least one reason and meets the configured minimum count.
 *
 * @returns Values to pass to {@link DenialQueryBucket}.
 */
export const useDenialQueryBucket = (): DenialQueryBucketDetails => {
  const { data: singleHealthFacilityDetails } = useSelector(
    (state: RootState) => state.singleHealthFacilityDetails,
  );
  const { denialQueryBucketData } = useDataAccumulatorForSingleOrder();
  const minReasonCount = singleHealthFacilityDetails?.reasons_min_count ?? 999;

  return useMemo(() => {
    if (!denialQueryBucketData) {
      return {
        shouldShowDenialQueryBucket: false,
        monthlyDenialText: "",
        totalDenialText: "",
        title: "",
        numberOfColumns: 0,
        accentColor: "",
        headerBackgroundColor: "",
        denialReasons: [],
        queryReasons: [],
        isDenialBucketGreaterThanZero: false,
        isQueryBucketGreaterThanZero: false,
      };
    }

    const denialReasons = denialQueryBucketData.denial_buckets ?? [];
    const queryReasons = denialQueryBucketData.query_buckets ?? [];
    const isDenialBucketGreaterThanZero = denialReasons.length > 0;
    const isQueryBucketGreaterThanZero = queryReasons.length > 0;
    const shouldShowDenialQueryBucket =
      (isDenialBucketGreaterThanZero &&
        denialReasons.length >= minReasonCount) ||
      (isQueryBucketGreaterThanZero && queryReasons.length >= minReasonCount);
    const isQueryOnly =
      !isDenialBucketGreaterThanZero && isQueryBucketGreaterThanZero;
    const hasBothBucketTypes =
      isDenialBucketGreaterThanZero && isQueryBucketGreaterThanZero;

    return {
      shouldShowDenialQueryBucket,
      monthlyDenialText: isQueryOnly
        ? `Monthly Query - ${denialQueryBucketData.month_total_query ?? "NA"}`
        : `Monthly Denial - ${
            denialQueryBucketData.month_total_denial ?? "NA"
          }`,
      totalDenialText: isQueryOnly
        ? `Total Query - ${denialQueryBucketData.total_query ?? "NA"}`
        : `Total Denial - ${denialQueryBucketData.total_denial ?? "NA"}`,
      title: hasBothBucketTypes
        ? "Denial Reasons and Queries for this Regimen"
        : isDenialBucketGreaterThanZero
          ? "Denial Reasons for this Regimen"
          : isQueryBucketGreaterThanZero
            ? "Query Reasons for this Regimen"
            : "",
      numberOfColumns: hasBothBucketTypes ? 2 : 1,
      accentColor: isQueryOnly ? "#0056D6" : "#CC0300",
      headerBackgroundColor: isQueryOnly ? "#E6F2FF" : "#FFF1F2",
      denialReasons,
      queryReasons,
      isDenialBucketGreaterThanZero,
      isQueryBucketGreaterThanZero,
    };
  }, [denialQueryBucketData, minReasonCount]);
};
